import type { Patient } from '@medplum/fhirtypes';
import { useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import { useToast } from '@eluve/components';

import {
  type PatientStatusAction,
  type PatientStatusDimension,
  statusActionFor,
} from './railPatient';

type UsePatientStatusActionsArgs = {
  tenantId: string;
  patientId: string;
  /** The rail's FHIR Patient; drives toggle direction and the optimistic lock. */
  patient: Patient | null;
  /** Called after any write settles server-side so the rail can refetch. */
  onSaved: () => void;
};

/**
 * Owns the rail's "Mark as deceased / left the practice" transitions
 * (ENG-5374): confirm-gated, reversible, written through the api-nest status
 * endpoint that updates FHIR and the Hasura search mirror together
 * (ENG-5377). `expectedVersionId` carries the optimistic lock end to end —
 * a 409 means the patient moved past the version the rail rendered. The
 * owning component renders the menu items and the confirm dialog as
 * siblings, wired to the returned state.
 */
export const usePatientStatusActions = ({
  tenantId,
  patientId,
  patient,
  onSaved,
}: UsePatientStatusActionsArgs) => {
  const apiClient = useApiClient();
  const toast = useToast();
  const [confirmAction, setConfirmAction] =
    useState<PatientStatusAction | null>(null);
  const [isPending, setIsPending] = useState(false);

  const requestAction = (dimension: PatientStatusDimension) => {
    if (patient) setConfirmAction(statusActionFor(patient, dimension));
  };

  const confirm = async () => {
    if (!confirmAction) return;
    setIsPending(true);
    try {
      const response = await apiClient.medplum.setPatientStatus({
        params: { tenantId, patientId },
        body: {
          status: confirmAction.dimension,
          set: confirmAction.set,
          expectedVersionId: patient?.meta?.versionId,
        },
      });

      if (response.status === 200) {
        toast.success('Patient status updated.');
        onSaved();
      } else if (response.status === 409) {
        toast.info(
          "This patient's status was just changed elsewhere. Refreshing.",
        );
        onSaved();
      } else {
        toast.error('Patient status could not be updated.');
      }
    } catch {
      toast.error('Patient status could not be updated.');
    } finally {
      setIsPending(false);
      setConfirmAction(null);
    }
  };

  return {
    confirmAction,
    isPending,
    requestAction,
    confirm: () => void confirm(),
    cancel: () => setConfirmAction(null),
  };
};
